import { useEffect, useState, type FormEvent } from 'react'
import { useAuth } from '../../auth'
import { api, ApiError } from '../../api'
import { useI18n } from '../../i18n'
import type { DisplayMode, Role, User, UserStatus } from '../../types'
import { Avatar, Empty, Modal, Spinner, useConfirm, useToast } from '../../components/ui'
import { IconPlus, IconTrash } from '../../components/icons'

const ROLES: Role[] = ['member', 'supervisor', 'admin']

export default function Team() {
  const { user, hasRole } = useAuth()
  const { t } = useI18n()
  const toast = useToast()
  const confirm = useConfirm()
  const isAdmin = hasRole('admin')
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [adding, setAdding] = useState(false)
  const [managing, setManaging] = useState<User | null>(null)

  async function load() {
    try {
      const r = await api.users()
      setUsers(r.users)
    } catch {
      toast.push(t('team.loadErr'), 'error')
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function changeRole(u: User, role: Role) {
    try {
      await api.updateUser(u.id, { role })
      setUsers((list) => list.map((x) => (x.id === u.id ? { ...x, role } : x)))
      toast.push(t('team.toast.roleUpdated'), 'ok')
    } catch (err) {
      toast.push(err instanceof ApiError ? err.message : t('team.updateErr'), 'error')
    }
  }

  async function toggleStatus(u: User) {
    const status: UserStatus = u.status === 'disabled' ? 'active' : 'disabled'
    try {
      await api.updateUser(u.id, { status })
      setUsers((list) => list.map((x) => (x.id === u.id ? { ...x, status } : x)))
      toast.push(status === 'disabled' ? t('team.toast.accessRevoked') : t('team.toast.accessRestored'), 'ok')
    } catch (err) {
      toast.push(err instanceof ApiError ? err.message : t('team.updateErr'), 'error')
    }
  }

  async function remove(u: User) {
    if (!(await confirm({ body: t('team.removeConfirm', { name: u.name || u.email }) }))) return
    try {
      await api.deleteUser(u.id)
      setUsers((list) => list.filter((x) => x.id !== u.id))
      toast.push(t('team.toast.removed'), 'ok')
    } catch (err) {
      toast.push(err instanceof ApiError ? err.message : t('team.updateErr'), 'error')
    }
  }

  if (loading) {
    return (
      <div className="full-loader" style={{ minHeight: 320 }}>
        <Spinner large />
      </div>
    )
  }

  return (
    <div>
      <div className="page-head between">
        <div>
          <h1>{t('team.title')}</h1>
          <p>{t('team.subtitle')}</p>
        </div>
        {isAdmin && (
          <button className="btn btn-primary" onClick={() => setAdding(true)}>
            <IconPlus width={18} height={18} /> {t('team.addPerson')}
          </button>
        )}
      </div>

      <div className="card" style={{ padding: 6 }}>
        <table className="table">
          <thead>
            <tr>
              <th>{t('team.col.person')}</th>
              <th>{t('team.col.role')}</th>
              <th>{t('team.col.account')}</th>
              {isAdmin && <th style={{ textAlign: 'right' }}>{t('team.col.actions')}</th>}
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id}>
                <td>
                  <div className="row">
                    <Avatar name={u.name} email={u.email} green={u.role !== 'member'} sm />
                    <div>
                      <div style={{ fontWeight: 700, color: 'var(--ink)' }}>
                        {u.name || '—'} {u.id === user?.id && <span className="tag">{t('common.you')}</span>}
                      </div>
                      <div className="muted" style={{ fontSize: 12.5 }}>{u.email}</div>
                    </div>
                  </div>
                </td>
                <td>
                  {isAdmin && u.id !== user?.id ? (
                    <select
                      className="input"
                      style={{ maxWidth: 150, padding: '8px 10px' }}
                      value={u.role}
                      onChange={(e) => changeRole(u, e.target.value as Role)}
                    >
                      {ROLES.map((r) => (
                        <option key={r} value={r}>
                          {t('role.' + r)}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className="badge purple">{t('role.' + u.role)}</span>
                  )}
                </td>
                <td>
                  {u.status === 'disabled' ? (
                    <span className="badge red">{t('team.status.disabled')}</span>
                  ) : u.has_password ? (
                    <span className="badge green">{t('team.status.active')}</span>
                  ) : (
                    <span className="badge grey">{t('team.status.invited')}</span>
                  )}
                </td>
                {isAdmin && (
                  <td>
                    <div className="row" style={{ justifyContent: 'flex-end' }}>
                      {u.id !== user?.id && (
                        <>
                          <button className="btn btn-ghost btn-sm" onClick={() => setManaging(u)}>
                            {t('team.manage')}
                          </button>
                          <button className="btn btn-ghost btn-sm" onClick={() => toggleStatus(u)}>
                            {u.status === 'disabled' ? t('team.enable') : t('team.disable')}
                          </button>
                          <button className="iconbtn" title={t('ap.remove')} onClick={() => remove(u)}>
                            <IconTrash width={17} height={17} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        {users.length === 0 && <Empty title={t('team.empty')} />}
      </div>

      {adding && (
        <AddPersonModal
          onClose={() => setAdding(false)}
          onAdded={(u) => {
            setUsers((list) => [...list, u])
            setAdding(false)
            toast.push(u.has_password ? t('team.toast.addedActive') : t('team.toast.added'), 'ok')
          }}
        />
      )}

      {managing && (
        <ManagePersonModal
          person={managing}
          onClose={() => setManaging(null)}
          onSaved={(patch) => {
            setUsers((list) => list.map((x) => (x.id === managing.id ? { ...x, ...patch } : x)))
            setManaging(null)
            toast.push(t('team.toast.saved'), 'ok')
          }}
        />
      )}
    </div>
  )
}

function AddPersonModal({ onClose, onAdded }: { onClose: () => void; onAdded: (u: User) => void }) {
  const { t } = useI18n()
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [role, setRole] = useState<Role>('member')
  const [password, setPassword] = useState('')
  const [largeMode, setLargeMode] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  async function submit(e: FormEvent) {
    e.preventDefault()
    setError('')
    if (password && password.length < 8) {
      setError(t('setpw.error.length'))
      return
    }
    const display_mode: DisplayMode = largeMode ? 'simple' : 'normal'
    setBusy(true)
    try {
      const { user } = await api.createUser({
        email: email.trim().toLowerCase(),
        name: name.trim(),
        role,
        display_mode,
        ...(password ? { password } : {}),
      })
      // The list badge reads has_password/display_mode, which the create
      // response doesn't echo back — fill them in from what we just sent.
      onAdded({ ...user, has_password: password ? 1 : 0, display_mode })
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('team.addErr'))
      setBusy(false)
    }
  }

  return (
    <Modal title={t('team.modal.title')} subtitle={t('team.modal.sub')} onClose={onClose}>
      <form className="auth-form" onSubmit={submit}>
        {error && <div className="alert error">{error}</div>}
        <div className="field">
          <label>{t('team.modal.email')}</label>
          <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </div>
        <div className="field">
          <label>{t('team.modal.name')}</label>
          <input className="input" type="text" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="field">
          <label>{t('team.modal.role')}</label>
          <select className="input" value={role} onChange={(e) => setRole(e.target.value as Role)}>
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {t('role.' + r)}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>{t('team.modal.password')} <span className="muted">({t('common.optional')})</span></label>
          <input
            className="input"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <span className="muted" style={{ fontSize: 13 }}>{t('team.modal.passwordHint')}</span>
        </div>
        <label className="check-row">
          <input type="checkbox" checked={largeMode} onChange={(e) => setLargeMode(e.target.checked)} />
          <span>{t('team.modal.largeMode')}</span>
        </label>
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? <Spinner /> : t('team.addPerson')}
          </button>
        </div>
      </form>
    </Modal>
  )
}

// Admin: set/reset a person's password (so they can sign in without the email
// link) and turn the large/simple mode on or off for them.
function ManagePersonModal({
  person,
  onClose,
  onSaved,
}: {
  person: User
  onClose: () => void
  onSaved: (patch: Partial<User>) => void
}) {
  const { t } = useI18n()
  const [password, setPassword] = useState('')
  const [largeMode, setLargeMode] = useState(person.display_mode === 'simple')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  async function submit(e: FormEvent) {
    e.preventDefault()
    setError('')
    if (password && password.length < 8) {
      setError(t('setpw.error.length'))
      return
    }
    const display_mode: DisplayMode = largeMode ? 'simple' : 'normal'
    setBusy(true)
    try {
      await api.updateUser(person.id, { display_mode, ...(password ? { password } : {}) })
      // Mirror the change into the list row (the response doesn't echo these).
      const patch: Partial<User> = { display_mode }
      if (password) {
        patch.has_password = 1
        patch.status = 'active'
      }
      onSaved(patch)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('team.updateErr'))
      setBusy(false)
    }
  }

  return (
    <Modal title={t('team.manage')} subtitle={person.name || person.email} onClose={onClose}>
      <form className="auth-form" onSubmit={submit}>
        {error && <div className="alert error">{error}</div>}
        <div className="field">
          <label>{t('team.modal.password')} <span className="muted">({t('common.optional')})</span></label>
          <input
            className="input"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <span className="muted" style={{ fontSize: 13 }}>{t('team.managePasswordHint')}</span>
        </div>
        <label className="check-row">
          <input type="checkbox" checked={largeMode} onChange={(e) => setLargeMode(e.target.checked)} />
          <span>{t('team.modal.largeMode')}</span>
        </label>
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? <Spinner /> : t('common.saveChanges')}
          </button>
        </div>
      </form>
    </Modal>
  )
}
